export interface WorkOrderRepairNote {
  date: string;
  notes: string;
  hours: string;
  travelMileage: string;
}

export function blankWorkOrderRepairNote(): WorkOrderRepairNote {
  return {
    date: '',
    notes: '',
    hours: '',
    travelMileage: '',
  };
}

export function normalizeWorkOrderRepairNote(raw: any): WorkOrderRepairNote {
  const n = raw && typeof raw === 'object' ? raw : {};
  return {
    date: n.date != null ? String(n.date) : '',
    notes: n.notes != null ? String(n.notes) : '',
    hours: n.hours != null ? String(n.hours) : '',
    travelMileage: n.travelMileage != null ? String(n.travelMileage) : '',
  };
}

export function normalizeWorkOrderRepairNotes(notes: any): WorkOrderRepairNote[] {
  if (!Array.isArray(notes)) return [];
  return notes.map(normalizeWorkOrderRepairNote);
}

export function isBlankWorkOrderRepairNote(n: WorkOrderRepairNote): boolean {
  return (
    !String(n.date || '').trim() &&
    !String(n.notes || '').trim() &&
    !String(n.hours || '').trim() &&
    !String(n.travelMileage || '').trim()
  );
}

/** Drop completely empty rows before persist. */
export function compactWorkOrderRepairNotes(notes: WorkOrderRepairNote[]): WorkOrderRepairNote[] {
  return normalizeWorkOrderRepairNotes(notes).filter((n) => !isBlankWorkOrderRepairNote(n));
}

/**
 * Aggregate structured rows into the legacy unitRepairNote string.
 * Format per non-empty row: "date — notes — hoursh — mileagemi" (omit empty segments).
 */
export function aggregateUnitRepairNote(notes: WorkOrderRepairNote[]): string {
  return compactWorkOrderRepairNotes(notes)
    .map((n) => {
      const parts: string[] = [];
      if (String(n.date || '').trim()) parts.push(String(n.date).trim());
      if (String(n.notes || '').trim()) parts.push(String(n.notes).trim());
      if (String(n.hours || '').trim()) parts.push(`${String(n.hours).trim()}h`);
      if (String(n.travelMileage || '').trim()) parts.push(`${String(n.travelMileage).trim()}mi`);
      return parts.join(' — ');
    })
    .filter(Boolean)
    .join('\n');
}

/**
 * Load UI rows: prefer structured unitRepairNotes; if empty and legacy
 * unitRepairNote (or cleanUnitNotes fallback) has text, seed first row Notes.
 */
export function loadWorkOrderRepairNotes(
  unitRepairNotes: any,
  unitRepairNote?: string | null,
  cleanUnitNotes?: string | null,
): WorkOrderRepairNote[] {
  const structured = compactWorkOrderRepairNotes(unitRepairNotes);
  if (structured.length) {
    return structured;
  }
  const legacy =
    (unitRepairNote != null && String(unitRepairNote).length)
      ? String(unitRepairNote)
      : (cleanUnitNotes != null ? String(cleanUnitNotes) : '');
  if (legacy.trim()) {
    return [{ date: '', notes: legacy, hours: '', travelMileage: '' }];
  }
  return [blankWorkOrderRepairNote()];
}